import { Badge, Group, LoadingOverlay, ModalProps, Text } from "@mantine/core"
import { DefaultModal } from "../../../-components/default-modal"
import { useForm } from "@mantine/form"
import { CreateUpdateChatForm } from "./CreateUpdateChatForm"
import { showErrorNotification } from "../../../utils/form-submission-notifications"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import {
  getGetChatByIdQueryKey,
  getGetChatsQueryKey,
  getUserHasArchivedChatQueryKey,
  useDeleteChat,
  useUpdateChat,
} from "@/orval/gen"
import {
  ChatDto,
  UpdateChat,
  CreateChatMember,
  ChatType,
} from "@/orval/gen/model"

type UpdateChatFormValues = {
  chatId?: number
  name?: string | null
  description?: string | null
  chatMembers: CreateChatMember[]
  chatType: ChatType
  isReadOnly: boolean
  autoMembershipFilterJson?: string | null
  isHidden: boolean
  groupProfilePictureId?: number | null
  isAutoAddMembersEnabled?: boolean
  isAutoRemoveMembersEnabled?: boolean
}

interface Props extends ModalProps {
  chat: ChatDto
  readonly: boolean
}

export function UpdateChatModal({ chat, readonly, ...props }: Props) {
  const initialValues: UpdateChatFormValues = {
    chatId: chat.chatId,
    name: chat.name,
    description: chat.description,
    chatType: chat.chatType as ChatType,
    isReadOnly: chat.isReadOnly,
    autoMembershipFilterJson: chat.autoMembershipFilterJson ?? null,
    isHidden: chat.isHidden ?? false,
    groupProfilePictureId: chat.groupProfilePictureId ?? null,
    chatMembers: chat.chatMembers
      .filter((member) => !member.isInactive)
      .map((member) => ({
        userId: member.userId,
        isAdmin: member.isAdmin,
        maySeeFullChat: member.lastChatPostBeforeJoiningId == null,
        isAddedAutomatically: member.isAddedAutomatically ?? false,
      })),
    isAutoAddMembersEnabled: chat.isAutoAddMembersEnabled ?? false,
    isAutoRemoveMembersEnabled: chat.isAutoRemoveMembersEnabled ?? false,
  }

  const form = useForm<UpdateChatFormValues>({
    mode: "controlled",
    initialValues,
  })

  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { mutateAsync, isPending } = useUpdateChat({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetChatByIdQueryKey({ chatId: chat.chatId }),
        })
        props.onClose()
      },
      onError: showErrorNotification,
    },
  })

  const { mutateAsync: deleteChat, isPending: isDeletePending } = useDeleteChat(
    {
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetChatsQueryKey(),
          })
          queryClient.invalidateQueries({
            queryKey: getUserHasArchivedChatQueryKey(),
          })
          props.onClose()
          if (["Feed", "BroadcastFeed"].includes(chat.chatType)) {
            navigate({ to: "/app/com/feeds" })
            return
          }
          navigate({ to: "/app/com/chat" })
        },
        onError: showErrorNotification,
      },
    }
  )

  const handleSubmit = async (values: UpdateChatFormValues) => {
    const payload: UpdateChat = {
      chatId: chat.chatId,
      name: values.name ?? "",
      description: values.description ?? "",
      chatMembers: values.chatMembers,
      chatType: values.chatType,
      isReadOnly: values.isReadOnly,
      autoMembershipFilterJson: values.autoMembershipFilterJson ?? "",
      isHidden: values.isHidden,
      isAutoAddMembersEnabled: values.isAutoAddMembersEnabled ?? false,
      isAutoRemoveMembersEnabled: values.isAutoRemoveMembersEnabled ?? false,
    }

    await mutateAsync({ data: payload })
  }
  const handleDelete = async () => {
    await deleteChat({ params: { chatId: chat.chatId } })
  }
  const { t } = useTranslation("app_server")

  return (
    <DefaultModal
      title={
        <Group>
          <Text>
            {t(`ChatType_${form.getValues().chatType}`) + ": " + chat.name}
          </Text>
          {chat.isHidden ? <Badge>{t("Hidden")}</Badge> : null}
        </Group>
      }
      {...props}
    >
      <LoadingOverlay visible={isPending} />
      <CreateUpdateChatForm
        readonly={readonly}
        form={form}
        initialAutoMembershipFilterJson={chat.autoMembershipFilterJson}
        inactiveMemberUserIds={chat.chatMembers
          .filter((member) => member.isInactive)
          .map((member) => member.userId)}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        isDeletePending={isDeletePending}
        onCancel={props.onClose}
      />
    </DefaultModal>
  )
}
